import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import AppShell from "@/components/AppShell";
import api from "@/lib/api";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
    CheckCircle2, Circle, Clock, FileText, Link2, ArrowLeft, ExternalLink,
    Plus, Trash2, Share2, ThumbsUp, MessageSquare, RotateCw, ChevronDown, ChevronUp, CornerDownRight, Bell, Eye,
    MailX, AlertTriangle
} from "lucide-react";
import { toast } from "sonner";
import ShareDialog from "@/components/ShareDialog";
import AddDeliverableDialog from "@/components/AddDeliverableDialog";
import AddFileDialog from "@/components/AddFileDialog";
import SubmitRevisionDialog from "@/components/SubmitRevisionDialog";
import ProgressEditor from "@/components/ProgressEditor";
import NotificationPrefsDialog from "@/components/NotificationPrefsDialog";
import NotificationDeliveryDialog from "@/components/NotificationDeliveryDialog";
import CommentsBlock from "@/components/CommentsBlock";
import PortalAccessDialog from "@/components/PortalAccessDialog";
import ClientHealthCard from "@/components/clients/ClientHealthCard";
import MeetingPrepPanel from "@/components/clients/MeetingPrepPanel";
import { displayTitle, revisionLabel } from "@/lib/naming";
import { formatDistanceToNow } from "date-fns";

export default function ClientPortal() {
    const { clientId } = useParams();
    const [client, setClient] = useState(null);
    const [activity, setActivity] = useState([]);
    const [loading, setLoading] = useState(true);
    const [shareOpen, setShareOpen] = useState(false);
    const [addDelivOpen, setAddDelivOpen] = useState(false);
    const [addFileOpen, setAddFileOpen] = useState(false);
    const [revisionFor, setRevisionFor] = useState(null); // deliverable object
    const [expandedHistory, setExpandedHistory] = useState({}); // deliverable_id -> bool
    const [prefsOpen, setPrefsOpen] = useState(false);
    const [deliveryOpen, setDeliveryOpen] = useState(false);
    const [accessOpen, setAccessOpen] = useState(false);
    const [activeTab, setActiveTab] = useState("overview");
    const navigate = useNavigate();

    const load = useCallback(async () => {
        try {
            const [{ data }, actRes] = await Promise.all([
                api.get(`/clients/${clientId}`),
                api.get(`/clients/${clientId}/activity`),
            ]);
            setClient(data);
            setActivity(actRes.data);
        } finally { setLoading(false); }
    }, [clientId]);

    useEffect(() => { load(); }, [load]);

    const deleteDeliverable = async (id) => {
        await api.delete(`/clients/${clientId}/deliverables/${id}`);
        toast.success("Deliverable removed");
        load();
    };

    const requestApprovalFor = async (deliverable) => {
        await api.patch(`/clients/${clientId}/deliverables/${deliverable.deliverable_id}`,
            { requires_approval: true });
        toast.success("Approval request sent to client");
        load();
    };

    const deleteFile = async (id) => {
        await api.delete(`/clients/${clientId}/files/${id}`);
        toast.success("File removed");
        load();
    };

    const p = client?.portal || {};
    const pending = useMemo(() => (p.approvals || []).filter(a => a.status === "pending"), [p]);
    // Helpers to work with versions
    const currentVersion = (d) => {
        const vs = d?.versions || [];
        if (!vs.length) return null;
        return vs.reduce((a, b) => (a.version_number > b.version_number ? a : b));
    };
    // Map approval_id -> version_number derived from deliverables[].versions[]
    const versionByApproval = useMemo(() => {
        const m = {};
        for (const d of (p.deliverables || [])) {
            for (const v of (d.versions || [])) {
                if (v.approval_id) m[v.approval_id] = v.version_number;
            }
        }
        return m;
    }, [p]);

    if (loading) return (
        <AppShell breadcrumbs={["Clients", "…"]}><div className="skeleton h-64 rounded-xl" /></AppShell>
    );
    if (!client) return (
        <AppShell breadcrumbs={["Clients", "Not found"]}><p className="text-slate-500">Client not found.</p></AppShell>
    );

    return (
        <AppShell breadcrumbs={["Clients", client.company_name]} onClientCreated={() => navigate("/clients")}>
            <div className="max-w-6xl">
                <button onClick={() => navigate("/clients")} data-testid="back-to-clients"
                    className="text-sm text-slate-500 hover:text-slate-900 inline-flex items-center gap-1 mb-6">
                    <ArrowLeft className="w-4 h-4" /> All clients
                </button>

                {/* Portal header — agency-side */}
                <div className="rounded-2xl border border-slate-200 bg-white p-8 mb-4 relative overflow-hidden">
                    <div className="pointer-events-none absolute -top-24 -right-24 w-64 h-64 rounded-full bg-[#C55A46]/10 blur-3xl" />
                    <p className="overline mb-2">Client portal</p>
                    <div className="flex items-start justify-between gap-6 flex-wrap">
                        <div>
                            <h1 className="font-display text-4xl tracking-tight text-slate-900">{client.company_name}</h1>
                            <p className="text-slate-500 mt-1">{client.project_name}</p>
                            <p className="text-xs text-slate-400 mt-3">Primary contact · {client.contact_name} ({client.contact_email})</p>
                        </div>
                        <div className="flex items-center gap-2">
                            {client.needs_attention && (
                                <span data-testid="portal-attention-pill" className="text-xs px-2 py-1 rounded-full bg-amber-50 text-amber-700 border border-amber-200">
                                    {client.attention_reason || "Needs attention"}
                                </span>
                            )}
                            <button data-testid="portal-access-btn"
                                onClick={() => setAccessOpen(true)}
                                title="Portal access log"
                                className="text-sm font-medium border border-slate-200 hover:border-slate-300 px-3 py-2 rounded-lg inline-flex items-center gap-2 text-slate-700 transition-colors">
                                <Eye className="w-4 h-4" /> Access
                            </button>
                            <button data-testid="portal-notifications-btn"
                                onClick={() => setPrefsOpen(true)}
                                title="Notification preferences"
                                className="text-sm font-medium border border-slate-200 hover:border-slate-300 px-3 py-2 rounded-lg inline-flex items-center gap-2 text-slate-700 transition-colors">
                                <Bell className="w-4 h-4" /> Notifications
                            </button>
                            <button data-testid="portal-delivery-btn"
                                onClick={() => setDeliveryOpen(true)}
                                title="Email delivery mode"
                                className="text-sm font-medium border border-slate-200 hover:border-slate-300 px-3 py-2 rounded-lg inline-flex items-center gap-2 text-slate-700 transition-colors">
                                <Clock className="w-4 h-4" /> Delivery
                            </button>
                            <button data-testid="portal-share-btn"
                                onClick={() => setShareOpen(true)}
                                className="btn-terra text-sm font-medium px-4 py-2 rounded-lg inline-flex items-center gap-2">
                                <Share2 className="w-4 h-4" /> Share portal
                            </button>
                        </div>
                    </div>
                </div>

                {/* Bounce Tracking P1 — email health banner. Rendered only when
                    email_health.status is `warning` or `undeliverable`. Field
                    stays "healthy" for every client until provider webhook
                    events are activated, so this banner is invisible today. */}
                <EmailHealthBanner client={client} />

                {/* AI P0 — Client Health card (deterministic; no LLM). */}
                <div className="mb-4">
                    <ClientHealthCard clientId={clientId} />
                </div>

                {/* AI Meeting Prep — renders only when an upcoming matched meeting exists. */}
                <MeetingPrepPanel clientId={clientId} onOpenTab={(tab) => setActiveTab(tab)} />

                <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
                    <TabsList className="bg-white border border-slate-200 rounded-lg p-1">
                        <TabsTrigger value="overview" data-testid="tab-overview" className="rounded-md">Overview</TabsTrigger>
                        <TabsTrigger value="progress" data-testid="tab-progress" className="rounded-md">Project Progress</TabsTrigger>
                        <TabsTrigger value="deliverables" data-testid="tab-deliverables" className="rounded-md">Deliverables</TabsTrigger>
                        <TabsTrigger value="approvals" data-testid="tab-approvals" className="rounded-md">
                            Approvals
                            {pending.length > 0 && (
                                <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700">{pending.length}</span>
                            )}
                        </TabsTrigger>
                        <TabsTrigger value="files" data-testid="tab-files" className="rounded-md">Files</TabsTrigger>
                        <TabsTrigger value="activity" data-testid="tab-activity" className="rounded-md">Activity</TabsTrigger>
                    </TabsList>

                    <TabsContent value="overview" className="mt-4">
                        <div className="grid md:grid-cols-3 gap-4">
                            <StatCard title="Status" value={p.overview?.status || "Active"} />
                            <StatCard title="Next milestone" value={p.overview?.next_milestone || "—"} />
                            <StatCard title="Overall progress" value={`${p.progress?.percent || 0}%`} />
                            <div className="md:col-span-3 rounded-xl border border-slate-200 bg-white p-6">
                                <p className="overline mb-2">Summary</p>
                                <p className="text-slate-700 leading-relaxed">{p.overview?.summary}</p>
                            </div>
                        </div>
                    </TabsContent>

                    <TabsContent value="progress" className="mt-4">
                        <ProgressEditor clientId={clientId} portal={p} onSaved={load} />
                    </TabsContent>

                    <TabsContent value="deliverables" className="mt-4">
                        <SectionHeader
                            title="Deliverables"
                            action={
                                <button data-testid="add-deliverable-btn" onClick={() => setAddDelivOpen(true)}
                                        className="btn-terra text-sm font-medium px-3 py-1.5 rounded-lg inline-flex items-center gap-1.5">
                                    <Plus className="w-3.5 h-3.5" /> Add deliverable
                                </button>
                            }
                        />
                        {(p.deliverables || []).length === 0 ? (
                            <EmptyBox text="No deliverables yet." />
                        ) : (
                            <div className="rounded-xl border border-slate-200 bg-white divide-y divide-slate-100">
                                {(p.deliverables || []).map((d) => {
                                    const cv = currentVersion(d);
                                    const isChanges = cv?.status === "changes_requested";
                                    const displayStatus = cv ? cv.status : d.status;
                                    const versions = (d.versions || []).slice().sort((a, b) => a.version_number - b.version_number);
                                    const showHistory = !!expandedHistory[d.deliverable_id];
                                    return (
                                    <div key={d.deliverable_id} data-testid={`deliverable-row-${d.deliverable_id}`}
                                         className="px-6 py-5">
                                        <div className="flex items-start gap-4">
                                            <div className="min-w-0 flex-1">
                                                <div className="flex items-center gap-2 flex-wrap">
                                                    <p className="text-sm font-medium text-slate-900">{displayTitle(d.title)}</p>
                                                    {cv && (
                                                        <span data-testid={`deliverable-version-${d.deliverable_id}`}
                                                              className="text-[10px] px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-600">
                                                            {revisionLabel(cv.version_number)}
                                                        </span>
                                                    )}
                                                </div>
                                                {d.description && <p className="text-xs text-slate-500 mt-1 leading-relaxed">{d.description}</p>}
                                                <div className="flex items-center gap-3 mt-2 text-xs text-slate-400">
                                                    {d.due && <span>Due {d.due}</span>}
                                                    {(cv?.file_name || d.file_name) && (
                                                        <span className="inline-flex items-center gap-1"><FileText className="w-3 h-3" />{cv?.file_name || d.file_name}</span>
                                                    )}
                                                    {(cv?.link || d.link) && (
                                                        <a href={cv?.link || d.link} target="_blank" rel="noreferrer"
                                                           className="inline-flex items-center gap-1 hover:text-slate-700">
                                                            <Link2 className="w-3 h-3" /> Link
                                                        </a>
                                                    )}
                                                    {d.requires_approval && !isChanges && cv?.status === "pending" && (
                                                        <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-[#FEF2F2] text-[#C55A46] border border-[#C55A46]/20">
                                                            Awaiting client approval
                                                        </span>
                                                    )}
                                                </div>
                                                {isChanges && (
                                                    <div
                                                        data-testid={`deliverable-feedback-${d.deliverable_id}`}
                                                        className="mt-3 rounded-lg border border-rose-200 bg-rose-50 p-3">
                                                        <div className="flex items-center gap-2">
                                                            <MessageSquare className="w-3.5 h-3.5 text-rose-600" />
                                                            <p className="text-xs font-semibold uppercase tracking-wider text-rose-700">Changes requested</p>
                                                            <span className="ml-auto text-[11px] text-rose-500">{formatSafe(cv.feedback_at)}</span>
                                                        </div>
                                                        <p
                                                            data-testid={`deliverable-feedback-text-${d.deliverable_id}`}
                                                            className="mt-1.5 text-sm text-rose-900 leading-relaxed whitespace-pre-wrap">
                                                            {cv.feedback}
                                                        </p>
                                                        <button
                                                            data-testid={`submit-revision-btn-${d.deliverable_id}`}
                                                            onClick={() => setRevisionFor(d)}
                                                            className="mt-3 btn-terra rounded-lg px-3 py-1.5 text-xs font-medium inline-flex items-center gap-1.5">
                                                            <RotateCw className="w-3.5 h-3.5" /> Submit Revision {(cv.version_number || 1) + 1}
                                                        </button>
                                                    </div>
                                                )}
                                                {cv && (
                                                    <CommentsBlock
                                                        comments={cv.comments || []}
                                                        versionLabel={revisionLabel(cv.version_number)}
                                                        testidPrefix={`comments-${d.deliverable_id}-${cv.version_id}`}
                                                        onSubmit={async (text) => {
                                                            const { data } = await api.post(
                                                                `/clients/${clientId}/deliverables/${d.deliverable_id}/versions/${cv.version_id}/comments`,
                                                                { text },
                                                            );
                                                            load();
                                                            return data;
                                                        }}
                                                    />
                                                )}
                                                {versions.length > 1 && (
                                                    <div className="mt-3">
                                                        <button
                                                            data-testid={`toggle-history-${d.deliverable_id}`}
                                                            onClick={() => setExpandedHistory({ ...expandedHistory, [d.deliverable_id]: !showHistory })}
                                                            className="text-xs font-medium text-slate-500 hover:text-slate-900 inline-flex items-center gap-1">
                                                            {showHistory ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                                                            Version history ({versions.length})
                                                        </button>
                                                        {showHistory && (
                                                            <ul data-testid={`version-history-${d.deliverable_id}`}
                                                                className="mt-3 space-y-3 border-l-2 border-slate-100 pl-4">
                                                                {versions.map((v) => (
                                                                    <li key={v.version_id} data-testid={`version-row-${d.deliverable_id}-${v.version_number}`}
                                                                        className="text-xs">
                                                                        <div className="flex items-center gap-2">
                                                                            <span className="text-slate-600 font-medium">{revisionLabel(v.version_number)}</span>
                                                                            <StatusPill status={v.status} />
                                                                            <span className="text-slate-400 ml-auto">{formatSafe(v.submitted_at)}</span>
                                                                        </div>
                                                                        {(v.file_name || v.link) && (
                                                                            <div className="mt-1 text-slate-500 flex items-center gap-2">
                                                                                {v.file_name && <span className="inline-flex items-center gap-1"><FileText className="w-3 h-3" />{v.file_name}</span>}
                                                                                {v.link && <a href={v.link} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 hover:text-slate-700"><Link2 className="w-3 h-3" /> Open</a>}
                                                                            </div>
                                                                        )}
                                                                        {v.feedback && (
                                                                            <div className="mt-1 text-rose-700 bg-rose-50 border border-rose-200 rounded p-2">
                                                                                <span className="font-medium">Client feedback:</span> {v.feedback}
                                                                            </div>
                                                                        )}
                                                                        {v.agency_reply && (
                                                                            <div className="mt-1 text-slate-700 bg-slate-50 border border-slate-200 rounded p-2 flex gap-2">
                                                                                <CornerDownRight className="w-3 h-3 mt-0.5 shrink-0 text-slate-400" />
                                                                                <div><span className="font-medium">Your reply:</span> {v.agency_reply}</div>
                                                                            </div>
                                                                        )}
                                                                        {v.version_id !== cv.version_id && (
                                                                            <CommentsBlock
                                                                                comments={v.comments || []}
                                                                                versionLabel={revisionLabel(v.version_number)}
                                                                                testidPrefix={`comments-${d.deliverable_id}-${v.version_id}`}
                                                                                onSubmit={async (text) => {
                                                                                    const { data } = await api.post(
                                                                                        `/clients/${clientId}/deliverables/${d.deliverable_id}/versions/${v.version_id}/comments`,
                                                                                        { text },
                                                                                    );
                                                                                    load();
                                                                                    return data;
                                                                                }}
                                                                            />
                                                                        )}
                                                                    </li>
                                                                ))}
                                                            </ul>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                            <StatusPill status={displayStatus} />
                                            <div className="flex items-center gap-1">
                                                {!d.requires_approval && d.status !== "approved" && (
                                                    <button onClick={() => requestApprovalFor(d)}
                                                            data-testid={`request-approval-${d.deliverable_id}`}
                                                            title="Request approval"
                                                            className="p-2 rounded-md text-slate-400 hover:text-[#C55A46] hover:bg-[#FEF2F2] transition-colors">
                                                        <ThumbsUp className="w-4 h-4" />
                                                    </button>
                                                )}
                                                <button onClick={() => deleteDeliverable(d.deliverable_id)}
                                                        data-testid={`delete-deliverable-${d.deliverable_id}`}
                                                        title="Delete"
                                                        className="p-2 rounded-md text-slate-400 hover:text-rose-600 hover:bg-rose-50 transition-colors">
                                                    <Trash2 className="w-4 h-4" />
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                    );
                                })}
                            </div>
                        )}
                    </TabsContent>

                    <TabsContent value="approvals" className="mt-4">
                        {(p.approvals || []).length === 0 ? (
                            <EmptyBox text="No approvals yet." />
                        ) : (
                            <div className="space-y-3">
                                {(p.approvals || []).map((a) => {
                                    const vNum = versionByApproval[a.approval_id];
                                    return (
                                    <div key={a.approval_id} data-testid={`approval-row-${a.approval_id}`}
                                         className="rounded-xl border border-slate-200 bg-white p-6">
                                        <div className="flex items-start justify-between gap-4">
                                            <div className="min-w-0">
                                                <div className="flex items-center gap-2 flex-wrap">
                                                    <p className="text-sm font-medium text-slate-900">{displayTitle(a.title)}</p>
                                                    {vNum != null && (
                                                        <span data-testid={`approval-version-${a.approval_id}`}
                                                              className="text-[10px] px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-600">
                                                            {revisionLabel(vNum)}
                                                        </span>
                                                    )}
                                                </div>
                                                <p className="text-xs text-slate-400 mt-1">
                                                    Requested {formatSafe(a.requested_at)}
                                                    {a.responded_at && ` · Responded ${formatSafe(a.responded_at)}`}
                                                </p>
                                                {a.feedback && (
                                                    <div className="mt-3 rounded-lg bg-amber-50 border border-amber-200 p-3 text-xs text-amber-800 inline-flex items-start gap-2">
                                                        <MessageSquare className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                                                        <span><span className="font-medium">Client feedback:</span> {a.feedback}</span>
                                                    </div>
                                                )}
                                            </div>
                                            <StatusPill status={a.status} />
                                        </div>
                                    </div>
                                    );
                                })}
                            </div>
                        )}
                    </TabsContent>

                    <TabsContent value="files" className="mt-4">
                        <SectionHeader
                            title="Files"
                            action={
                                <button data-testid="add-file-btn" onClick={() => setAddFileOpen(true)}
                                        className="btn-terra text-sm font-medium px-3 py-1.5 rounded-lg inline-flex items-center gap-1.5">
                                    <Plus className="w-3.5 h-3.5" /> Add file
                                </button>
                            }
                        />
                        <div className="rounded-xl border border-slate-200 bg-white divide-y divide-slate-100">
                            {(p.files || []).length === 0 && (
                                <div className="p-8 text-slate-500 text-center text-sm">No files uploaded yet.</div>
                            )}
                            {(p.files || []).map((f) => (
                                <div key={f.file_id} data-testid={`file-row-${f.file_id}`}
                                     className="flex items-center gap-4 px-6 py-4">
                                    <div className="w-9 h-9 rounded-lg bg-slate-50 flex items-center justify-center"><FileText className="w-4 h-4 text-slate-500" /></div>
                                    <div className="min-w-0 flex-1">
                                        <p className="text-sm font-medium text-slate-900 truncate">{f.name}</p>
                                        <p className="text-xs text-slate-400">
                                            {f.size}{f.size && f.note ? " · " : ""}{f.note}
                                        </p>
                                    </div>
                                    {f.link && (
                                        <a href={f.link} target="_blank" rel="noreferrer"
                                           className="text-slate-500 hover:text-slate-900 p-2 rounded-md inline-flex items-center gap-1 text-xs font-medium">
                                            Open <ExternalLink className="w-3.5 h-3.5" />
                                        </a>
                                    )}
                                    <button onClick={() => deleteFile(f.file_id)}
                                            data-testid={`delete-file-${f.file_id}`}
                                            className="p-2 rounded-md text-slate-400 hover:text-rose-600 hover:bg-rose-50 transition-colors">
                                        <Trash2 className="w-4 h-4" />
                                    </button>
                                </div>
                            ))}
                        </div>
                    </TabsContent>

                    <TabsContent value="activity" className="mt-4">
                        <div className="rounded-xl border border-slate-200 bg-white p-6">
                            <p className="overline mb-4">Portal activity</p>
                            {activity.length === 0 ? (
                                <p className="text-slate-500 text-sm">No activity yet.</p>
                            ) : (
                                <ul className="space-y-4">
                                    {activity.map((a) => (
                                        <li key={a.activity_id} className="flex gap-3">
                                            <div className={`w-1.5 h-1.5 rounded-full mt-2 shrink-0 ${
                                                a.kind === "approval_approved" ? "bg-emerald-500" :
                                                a.kind === "changes_requested" ? "bg-rose-500" :
                                                a.kind === "approval_requested" ? "bg-amber-500" :
                                                a.kind === "email_sent" ? "bg-emerald-400" :
                                                a.kind === "email_failed" ? "bg-rose-400" :
                                                a.kind === "portal_created" ? "bg-[#C55A46]" :
                                                "bg-slate-400"
                                            }`} />
                                            <div className="min-w-0">
                                                <div className="flex items-center gap-2 flex-wrap">
                                                    <p className="text-sm text-slate-700 leading-snug">{a.message}</p>
                                                    {/* Bounce Tracking P1 — small pill next to email rows so
                                                        operators can see delivery outcome at a glance. */}
                                                    {a.kind === "email_sent" && (
                                                        <span data-testid={`activity-delivery-pill-${a.activity_id}`}
                                                            className="text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 whitespace-nowrap">Accepted</span>
                                                    )}
                                                    {a.kind === "email_failed" && (
                                                        <span data-testid={`activity-delivery-pill-${a.activity_id}`}
                                                            className="text-[10px] px-1.5 py-0.5 rounded-full bg-rose-50 text-rose-700 border border-rose-200 whitespace-nowrap">Send failed</span>
                                                    )}
                                                </div>
                                                <p className="text-xs text-slate-400 mt-0.5">{formatSafe(a.created_at)}</p>
                                            </div>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    </TabsContent>
                </Tabs>
            </div>

            <ShareDialog open={shareOpen} onOpenChange={setShareOpen} client={client} />
            <AddDeliverableDialog open={addDelivOpen} onOpenChange={setAddDelivOpen}
                                  clientId={clientId} onCreated={() => { setAddDelivOpen(false); load(); }} />
            <AddFileDialog open={addFileOpen} onOpenChange={setAddFileOpen}
                           clientId={clientId} onCreated={() => { setAddFileOpen(false); load(); }} />
            <SubmitRevisionDialog
                open={!!revisionFor}
                onOpenChange={(v) => !v && setRevisionFor(null)}
                clientId={clientId}
                deliverable={revisionFor}
                feedback={revisionFor ? currentVersion(revisionFor)?.feedback : ""}
                onCreated={() => { setRevisionFor(null); load(); }}
            />
            <NotificationPrefsDialog
                open={prefsOpen}
                onOpenChange={setPrefsOpen}
                clientId={clientId}
                clientName={client.company_name}
            />
            <NotificationDeliveryDialog
                open={deliveryOpen}
                onOpenChange={setDeliveryOpen}
                clientId={clientId}
                clientName={client.company_name}
            />
            <PortalAccessDialog
                open={accessOpen}
                onOpenChange={setAccessOpen}
                clientId={clientId}
                clientName={client.company_name}
            />
        </AppShell>
    );
}

function StatCard({ title, value }) {
    return (
        <div className="rounded-xl border border-slate-200 bg-white p-6">
            <p className="overline mb-2">{title}</p>
            <p className="font-display text-2xl tracking-tight text-slate-900">{value}</p>
        </div>
    );
}

// Bounce Tracking P1 — client-detail email health banner. Rendered ONLY when
// email_health.status is `warning` or `undeliverable`. The field is "healthy"
// for every client today (provider webhooks are dormant), so this component
// currently returns null in practice — the code path exists so activation of
// bounce events is a config flip, not a UI change.
function EmailHealthBanner({ client }) {
    const status = client?.email_health?.status;
    if (status !== "warning" && status !== "undeliverable") return null;
    const undeliverable = status === "undeliverable";
    const reason = client?.email_health?.last_bounce_reason;
    return (
        <div
            data-testid={`portal-email-health-banner-${status}`}
            className={`mb-4 rounded-xl border p-4 flex items-start gap-3 ${
                undeliverable
                    ? "border-rose-200 bg-rose-50/70"
                    : "border-amber-200 bg-amber-50/70"
            }`}>
            <div className={`mt-0.5 shrink-0 w-9 h-9 rounded-full bg-white border flex items-center justify-center ${
                undeliverable ? "border-rose-200 text-rose-600" : "border-amber-200 text-amber-600"
            }`}>
                {undeliverable ? <MailX className="w-4 h-4" /> : <AlertTriangle className="w-4 h-4" />}
            </div>
            <div className="min-w-0 flex-1">
                <p className={`font-medium text-sm ${undeliverable ? "text-rose-800" : "text-amber-800"}`}>
                    {undeliverable
                        ? "This client's email address is currently undeliverable"
                        : "This client's email address may be having delivery issues"}
                </p>
                <p className={`text-xs mt-1 ${undeliverable ? "text-rose-700" : "text-amber-700"}`}>
                    Recipient: <span className="font-medium">{client.contact_email}</span>
                    {reason ? <> · Last provider response: <span className="italic">"{reason}"</span></> : null}
                </p>
                <p className="text-xs text-slate-500 mt-2">
                    {undeliverable
                        ? "New non-urgent notifications will not be delivered to this address. Update the contact email in Notifications to re-evaluate."
                        : "We're monitoring. If delivery keeps failing, this will become 'undeliverable' automatically."}
                </p>
            </div>
        </div>
    );
}

function PhaseChip({ phase }) {
    const icon = phase.status === "complete" ? <CheckCircle2 className="w-4 h-4 text-emerald-500" />
        : phase.status === "in_progress" ? <Clock className="w-4 h-4 text-[#C55A46]" />
        : <Circle className="w-4 h-4 text-slate-300" />;
    return (
        <div className="rounded-lg border border-slate-200 p-4 flex items-center gap-3">
            {icon}
            <div>
                <p className="text-sm font-medium text-slate-900">{phase.name}</p>
                <p className="text-xs text-slate-500 capitalize">{phase.status.replace("_", " ")}</p>
            </div>
        </div>
    );
}

function StatusPill({ status }) {
    const tone = status === "delivered" || status === "approved" ? "emerald"
        : status === "review" || status === "pending" ? "amber"
        : status === "changes_requested" ? "rose"
        : "slate";
    const cls = tone === "emerald" ? "bg-emerald-50 text-emerald-700 border-emerald-200"
        : tone === "amber" ? "bg-amber-50 text-amber-700 border-amber-200"
        : tone === "rose" ? "bg-rose-50 text-rose-700 border-rose-200"
        : "bg-slate-50 text-slate-600 border-slate-200";
    return <span className={`text-xs px-2 py-1 rounded-full capitalize border shrink-0 ${cls}`}>{status.replace("_", " ")}</span>;
}

function SectionHeader({ title, action }) {
    return (
        <div className="flex items-center justify-between mb-3">
            <p className="overline">{title}</p>
            {action}
        </div>
    );
}

function EmptyBox({ text }) {
    return <div className="rounded-xl border border-slate-200 bg-white p-8 text-slate-500 text-sm text-center">{text}</div>;
}

function formatSafe(iso) {
    if (!iso) return "";
    try { return formatDistanceToNow(new Date(iso), { addSuffix: true }); }
    catch { return iso; }
}
